import type {
  AssignShift,
  AttrSetting,
  HourlyDto,
  ImportCommit,
  ImportKind,
  ImportPreviewDto,
  ImportResultDto,
  MyShiftDto,
  ProdCardDto,
  Stage,
  TimeEventDto,
  ActionDto,
  ActionInput,
  ProductDto,
  ProductImportResult,
  ProductListDto,
  ShiftInput,
  TaskDto,
  TaskInput,
  TaskPatch,
  ProcedureItem,
  CheckDto,
  CheckState,
  PositionDto,
  PositionInput,
  AreaDto,
  AreaInput,
  EmployeeDto,
  EmployeeInput,
  EmployeePatch,
  EquipmentDetailDto,
  EquipmentDto,
  EquipmentFileDto,
  EquipmentInput,
  LoginAttemptDto,
  VncSessionDetailDto,
  MeDto,
  PanelAdminDto,
  PanelCheckDto,
  PanelDto,
  PanelInput,
  VncMode,
  VncSessionDto,
  VncTicketDto,
} from '@app/shared';
import { platform } from './platform';

export class ApiError extends Error {
  status: number;
  code: string;
  constructor(status: number, code: string, message: string) {
    super(message);
    this.status = status;
    this.code = code;
  }
}

/** Картинка из API (снимки экрана): <img> не умеет передавать авторизацию, поэтому через fetch → blob. */
export async function fetchImageUrl(path: string): Promise<string> {
  const res = await fetch(`/api${path}`, { headers: { authorization: platform.authHeader() } });
  if (!res.ok) throw new ApiError(res.status, 'error', 'Снимок не загрузился');
  return URL.createObjectURL(await res.blob());
}

/**
 * Фото и видео оборудования. Ссылок на файлы нет: файл запрашивается с авторизацией и показывается
 * из памяти (адрес blob: живёт только внутри открытого приложения — скопировать и открыть его в другом месте нельзя).
 * Фото запоминаются на время работы приложения, чтобы не грузить список заново.
 */
const imageCache = new Map<string, Promise<string>>();
export function authImageUrl(path: string): Promise<string> {
  let p = imageCache.get(path);
  if (!p) {
    p = fetchBlobUrl(path);
    p.catch(() => imageCache.delete(path));
    imageCache.set(path, p);
  }
  return p;
}

/** Файл из API → адрес blob:, с ходом загрузки (видео бывают большими). Освободить: URL.revokeObjectURL. */
export async function fetchBlobUrl(path: string, onProgress?: (share: number) => void): Promise<string> {
  let res: Response;
  try {
    res = await fetch(`/api${path}`, { headers: { authorization: platform.authHeader() } });
  } catch {
    throw new ApiError(0, 'network', 'Нет связи с сервером. Проверьте интернет.');
  }
  if (!res.ok || !res.body) {
    const data = (await res.json().catch(() => ({}))) as { error?: string; message?: string };
    throw new ApiError(res.status, data.error ?? 'error', data.message ?? 'Файл не открылся');
  }
  const total = Number(res.headers.get('content-length')) || 0;
  const type = res.headers.get('content-type') ?? 'application/octet-stream';
  if (!onProgress || !total) return URL.createObjectURL(new Blob([await res.arrayBuffer()], { type }));
  const reader = res.body.getReader();
  const parts: Uint8Array[] = [];
  let got = 0;
  for (;;) {
    const { done, value } = await reader.read();
    if (done) break;
    parts.push(value);
    got += value.length;
    onProgress(got / total);
  }
  return URL.createObjectURL(new Blob(parts as BlobPart[], { type }));
}

/** Файл оборудования целиком (для просмотра PDF внутри приложения) — с авторизацией, без ссылки. */
export async function fetchEquipmentFile(equipmentId: string, fileId: string): Promise<ArrayBuffer> {
  let res: Response;
  try {
    void equipmentId;
    res = await fetch(`/api/files/${fileId}`, { headers: { authorization: platform.authHeader() } });
  } catch {
    throw new ApiError(0, 'network', 'Нет связи с сервером. Проверьте интернет.');
  }
  if (!res.ok) {
    const data = (await res.json().catch(() => ({}))) as { error?: string; message?: string };
    throw new ApiError(res.status, data.error ?? 'error', data.message ?? 'Файл не открылся');
  }
  return res.arrayBuffer();
}

async function request<T>(method: string, path: string, body?: unknown): Promise<T> {
  let res: Response;
  try {
    res = await fetch(`/api${path}`, {
      method,
      headers: { authorization: platform.authHeader(), ...(body !== undefined ? { 'content-type': 'application/json' } : {}) },
      body: body !== undefined ? JSON.stringify(body) : undefined,
    });
  } catch {
    throw new ApiError(0, 'network', 'Нет связи с сервером. Проверьте интернет.');
  }
  if (res.status === 204) return undefined as T;
  const data = (await res.json().catch(() => ({}))) as { error?: string; message?: string };
  if (!res.ok) throw new ApiError(res.status, data.error ?? 'error', data.message ?? 'Что-то пошло не так');
  return data as T;
}

/** Адрес WebSocket для билета: тот же сайт, wss на https. */
export function vncUrl(path: string): string {
  return `${location.protocol === 'https:' ? 'wss:' : 'ws:'}//${location.host}${path}`;
}

export const api = {
  me: () => request<MeDto>('GET', '/me'),

  staff: () => request<EmployeeDto[]>('GET', '/staff'),
  loginAttempts: () => request<LoginAttemptDto[]>('GET', '/staff/attempts'),
  createEmployee: (e: EmployeeInput) => request<EmployeeDto>('POST', '/staff', e),
  patchEmployee: (id: string, patch: EmployeePatch) => request<EmployeeDto>('PATCH', `/staff/${id}`, patch),
  deleteEmployee: (id: string) => request<void>('DELETE', `/staff/${id}`),
  positions: () => request<PositionDto[]>('GET', '/positions'),
  createPosition: (p: PositionInput) => request<PositionDto>('POST', '/positions', p),
  updatePosition: (id: string, p: Partial<PositionInput>) => request<PositionDto>('PATCH', `/positions/${id}`, p),
  deletePosition: (id: string) => request<void>('DELETE', `/positions/${id}`),

  areas: () => request<AreaDto[]>('GET', '/areas'),
  createArea: (a: AreaInput) => request<AreaDto>('POST', '/areas', a),
  updateArea: (id: string, a: Partial<AreaInput>) => request<AreaDto>('PATCH', `/areas/${id}`, a),
  deleteArea: (id: string) => request<void>('DELETE', `/areas/${id}`),

  panels: () => request<PanelDto[]>('GET', '/panels'),
  adminPanels: () => request<PanelAdminDto[]>('GET', '/admin/panels'),
  createPanel: (p: PanelInput) => request<PanelAdminDto>('POST', '/panels', p),
  updatePanel: (id: string, p: Partial<PanelInput>) => request<PanelAdminDto>('PATCH', `/panels/${id}`, p),
  deletePanel: (id: string) => request<void>('DELETE', `/panels/${id}`),
  checkPanel: (id: string | null, p: Partial<Pick<PanelInput, 'host' | 'port' | 'password'>>) =>
    request<PanelCheckDto>('POST', id ? `/panels/${id}/check` : '/panels/check', p),
  session: (id: string, mode: VncMode) => request<VncTicketDto>('POST', `/panels/${id}/session`, { mode }),
  sessions: (f: { panelId?: string; employeeId?: string; from?: string; to?: string } = {}) => {
    const q = new URLSearchParams(Object.entries(f).filter((e): e is [string, string] => !!e[1])).toString();
    return request<VncSessionDto[]>('GET', `/sessions${q ? `?${q}` : ''}`);
  },
  sessionDetail: (id: string) => request<VncSessionDetailDto>('GET', `/sessions/${id}`),
  /** Снимок как есть (image/jpeg), без base64. */
  saveShot: async (id: string, jpeg: Blob) => {
    const res = await fetch(`/api/sessions/${id}/shots`, { method: 'POST', headers: { authorization: platform.authHeader(), 'content-type': 'image/jpeg' }, body: jpeg });
    if (res.status === 415) {
      // Сервер старой версии принимает только JSON {dataUrl}.
      const dataUrl = await new Promise<string>((resolve, reject) => {
        const r = new FileReader();
        r.onload = () => resolve(String(r.result));
        r.onerror = () => reject(r.error);
        r.readAsDataURL(jpeg);
      });
      await request<unknown>('POST', `/sessions/${id}/shots`, { dataUrl });
      return;
    }
    if (!res.ok && res.status !== 204) throw new ApiError(res.status, 'error', 'Снимок не сохранился');
  },
  terminate: (id: string) => request<{ ok: boolean }>('POST', `/sessions/${id}/terminate`, {}),

  /* Проверки по спискам действий */
  checks: (f: { today?: boolean; issues?: boolean; days?: number } = {}) => {
    const q = new URLSearchParams({ ...(f.today ? { today: '1' } : {}), ...(f.issues ? { issues: '1' } : {}), ...(f.days ? { days: String(f.days) } : {}) }).toString();
    return request<CheckDto[]>('GET', `/checks${q ? `?${q}` : ''}`);
  },
  startCheck: (equipmentId: string, procedureId: string) => request<CheckDto>('POST', `/equipment/${equipmentId}/checks`, { procedureId }),
  check: (id: string) => request<CheckDto>('GET', `/checks/${id}`),
  markCheckItem: (id: string, itemId: string, patch: { state: CheckState | null; note?: string | null }) =>
    request<CheckDto>('PATCH', `/checks/${id}/items/${encodeURIComponent(itemId)}`, patch),
  finishCheck: (id: string) => request<CheckDto>('POST', `/checks/${id}/finish`, {}),
  cancelCheck: (id: string) => request<void>('DELETE', `/checks/${id}`),
  deleteCheckPhoto: (id: string, photoId: string) => request<CheckDto>('DELETE', `/checks/${id}/photos/${photoId}`),
  async uploadCheckPhoto(id: string, itemId: string, file: Blob): Promise<CheckDto> {
    let res: Response;
    try {
      res = await fetch(`/api/checks/${id}/items/${encodeURIComponent(itemId)}/photos?type=${encodeURIComponent(file.type || 'image/jpeg')}`, {
        method: 'POST',
        headers: { authorization: platform.authHeader(), 'content-type': 'application/octet-stream' },
        body: file,
      });
    } catch {
      throw new ApiError(0, 'network', 'Связь прервалась — фото не загрузилось');
    }
    const data = (await res.json().catch(() => ({}))) as { error?: string; message?: string };
    if (!res.ok) throw new ApiError(res.status, data.error ?? 'error', data.message ?? 'Фото не загрузилось');
    return data as CheckDto;
  },

  /* Доска задач и задание на смену */
  tasks: () => request<TaskDto[]>('GET', '/tasks'),
  task: (id: string) => request<TaskDto>('GET', `/tasks/${id}`),
  createTask: (t: TaskInput) => request<TaskDto>('POST', '/tasks', t),
  patchTask: (id: string, t: TaskPatch) => request<TaskDto>('PATCH', `/tasks/${id}`, t),
  deleteTask: (id: string) => request<void>('DELETE', `/tasks/${id}`),
  startTask: (id: string) => request<TaskDto>('POST', `/tasks/${id}/start`, {}),
  createShift: (s: ShiftInput) => request<{ created: number }>('POST', '/shifts', s),
  people: () => request<{ id: string; name: string; position: string | null; areaIds: string[] }[]>('GET', '/people'),

  /* Производство: доска этапов, смена, журнал времени, выработка */
  cards: () => request<ProdCardDto[]>('GET', '/cards'),
  card: (id: string) => request<ProdCardDto>('GET', `/cards/${id}`),
  moveCard: (id: string, stage: Stage) => request<ProdCardDto>('POST', `/cards/${id}/move`, { stage }),
  assignCard: (id: string, a: AssignShift) => request<ProdCardDto>('POST', `/cards/${id}/assign`, a),
  myShift: () => request<MyShiftDto>('GET', '/my-shift'),
  closeShift: (id: string, totals: { taskId: string; qty: number }[], note?: string | null) =>
    request<{ equipment: { id: string; name: string }[] }>('POST', `/shifts/${id}/close`, { totals, note }),
  startEvent: (e: { kind: string; taskId?: string | null; equipmentId?: string | null; title?: string | null }) => request<TimeEventDto>('POST', '/events', e),
  finishEvent: (id: string, f: { comment?: string | null; fileIds?: string[] }) => request<TimeEventDto>('POST', `/events/${id}/finish`, f),
  downtime: (d: { reason: string; comment?: string | null; fileIds?: string[] }) => request<TimeEventDto>('POST', '/downtime', d),
  output: (o: { taskId: string; qty: number; mode: 'hour' | 'total' }) => request<{ done: number; doneShift: number; added: number }>('POST', '/output', o),
  hourly: (day?: string) => request<HourlyDto>('GET', `/hourly${day ? `?day=${day}` : ''}`),
  attrs: () => request<AttrSetting[]>('GET', '/settings/attrs'),
  setAttrs: (a: AttrSetting[]) => request<AttrSetting[]>('PUT', '/settings/attrs', a),
  async importPreview(kind: ImportKind, file: File): Promise<ImportPreviewDto> {
    let res: Response;
    try {
      res = await fetch(`/api/imports/${kind}/preview?name=${encodeURIComponent(file.name)}`, {
        method: 'POST',
        headers: { authorization: platform.authHeader(), 'content-type': 'application/octet-stream' },
        body: file,
      });
    } catch {
      throw new ApiError(0, 'network', 'Связь прервалась — файл не загрузился');
    }
    const data = (await res.json().catch(() => ({}))) as { error?: string; message?: string };
    if (!res.ok) throw new ApiError(res.status, data.error ?? 'error', data.message ?? 'Файл не прочитался');
    return data as ImportPreviewDto;
  },
  importCommit: (kind: ImportKind, c: ImportCommit) => request<ImportResultDto>('POST', `/imports/${kind}/commit`, c),

  /* Номенклатура */
  products: (q = '') => request<ProductListDto[]>('GET', `/products${q ? `?q=${encodeURIComponent(q)}` : ''}`),
  product: (id: string) => request<ProductDto>('GET', `/products/${id}`),
  updateProduct: (id: string, p: { name?: string; specs?: { k: string; v: string }[]; changeover?: ProcedureItem[] }) => request<ProductDto>('PATCH', `/products/${id}`, p),
  async importProducts(file: File): Promise<ProductImportResult> {
    let res: Response;
    try {
      res = await fetch(`/api/products/import?name=${encodeURIComponent(file.name)}`, {
        method: 'POST',
        headers: { authorization: platform.authHeader(), 'content-type': 'application/octet-stream' },
        body: file,
      });
    } catch {
      throw new ApiError(0, 'network', 'Связь прервалась — файл не загрузился');
    }
    const data = (await res.json().catch(() => ({}))) as { error?: string; message?: string };
    if (!res.ok) throw new ApiError(res.status, data.error ?? 'error', data.message ?? 'Файл не загрузился');
    return data as ProductImportResult;
  },

  /* Типовые действия */
  actions: () => request<ActionDto[]>('GET', '/actions'),
  createAction: (a: ActionInput) => request<ActionDto>('POST', '/actions', a),
  updateAction: (id: string, a: Partial<ActionInput>) => request<ActionDto>('PATCH', `/actions/${id}`, a),
  deleteAction: (id: string) => request<void>('DELETE', `/actions/${id}`),
  /** Общий файл (библиотека, переналадка) — без привязки к машине. */
  uploadSharedFile(file: File, title: string, onProgress: (share: number) => void): Promise<EquipmentFileDto> {
    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      xhr.open('POST', `/api/files?${new URLSearchParams({ type: file.type || 'application/octet-stream', title })}`);
      xhr.setRequestHeader('authorization', platform.authHeader());
      xhr.setRequestHeader('content-type', 'application/octet-stream');
      xhr.upload.onprogress = (e) => e.lengthComputable && onProgress(e.loaded / e.total);
      xhr.onload = () => {
        let data: { message?: string; error?: string } = {};
        try {
          data = JSON.parse(xhr.responseText) as typeof data;
        } catch {
          /* не JSON */
        }
        if (xhr.status >= 200 && xhr.status < 300) resolve(data as unknown as EquipmentFileDto);
        else reject(new ApiError(xhr.status, data.error ?? 'error', data.message ?? 'Файл не загрузился'));
      };
      xhr.onerror = () => reject(new ApiError(0, 'network', 'Связь прервалась — файл не загрузился'));
      xhr.send(file);
    });
  },

  equipment: () => request<EquipmentDto[]>('GET', '/equipment'),
  equipmentDetail: (id: string) => request<EquipmentDetailDto>('GET', `/equipment/${id}`),
  equipmentByQr: (qr: string) => request<{ id: string; name: string; code: string }>('GET', `/equipment/by-qr/${encodeURIComponent(qr)}`),
  createEquipment: (e: EquipmentInput) => request<EquipmentDto>('POST', '/equipment', e),
  updateEquipment: (id: string, e: Partial<EquipmentInput>) => request<EquipmentDto>('PATCH', `/equipment/${id}`, e),
  deleteEquipment: (id: string) => request<void>('DELETE', `/equipment/${id}`),
  /** Порядок в списке: id сверху вниз. */
  orderEquipment: (ids: string[]) => request<void>('POST', '/equipment/order', { ids }),
  deleteEquipmentFile: (id: string, fileId: string) => request<void>('DELETE', `/equipment/${id}/files/${fileId}`),
  /** Файл оборудования — как есть (без base64), с ходом загрузки: видео бывают большими. */
  uploadEquipmentFile(id: string, file: File, title: string, onProgress: (share: number) => void, avatar = false, step = false): Promise<EquipmentFileDto> {
    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      const q = new URLSearchParams({ type: file.type || 'application/octet-stream', title, ...(avatar ? { avatar: '1' } : {}), ...(step ? { step: '1' } : {}) });
      xhr.open('POST', `/api/equipment/${id}/files?${q}`);
      xhr.setRequestHeader('authorization', platform.authHeader());
      xhr.setRequestHeader('content-type', 'application/octet-stream');
      xhr.upload.onprogress = (e) => e.lengthComputable && onProgress(e.loaded / e.total);
      xhr.onload = () => {
        let data: { message?: string; error?: string } = {};
        try {
          data = JSON.parse(xhr.responseText) as typeof data;
        } catch {
          /* не JSON */
        }
        if (xhr.status >= 200 && xhr.status < 300) resolve(data as unknown as EquipmentFileDto);
        else if (xhr.status === 413) reject(new ApiError(413, 'too_large', 'Файл слишком большой'));
        else reject(new ApiError(xhr.status, data.error ?? 'error', data.message ?? 'Файл не загрузился'));
      };
      xhr.onerror = () => reject(new ApiError(0, 'network', 'Связь прервалась — файл не загрузился'));
      xhr.send(file);
    });
  },
};
